import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router';
import type { PlanLinkDto } from '@tiffinify/contracts';
import { formatDay, formatPhone } from '@tiffinify/core';
import { LoadError, PageLoader, useConfirm } from '../components/ui';
import { Back, Btn, Card as PCard, Group, Hero, Ic, Kick, Note, Row, Screen, Title, Top } from '../components/p';
import { post } from '../lib/api';
import { clock12, errorMessage, formatInr } from '../lib/format';
import { WhatsAppText } from '../lib/whatsapp';
import { useProfile, useSettings } from '../lib/queries';

/** The customer's "My plan" page: what they'll see, the message that carries the link, and WhatsApp. */
export function PlanLink() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { confirm, sheet: confirmSheet } = useConfirm();
  const profile = useProfile(id);
  const cutoff = useSettings().data?.pauseRequestCutoff ?? '23:59';
  // Asking twice gives the same link: there is one per customer.
  const q = useQuery({ queryKey: ['plan-link', id], queryFn: () => post<PlanLinkDto>(`/subscribers/${id}/plan-link`), staleTime: Infinity });
  const reset = useMutation({
    mutationFn: () => post<PlanLinkDto>(`/subscribers/${id}/plan-link?reset=1`),
    onSuccess: (res) => { qc.setQueryData(['plan-link', id], res); setNote('New link made. The old one no longer opens.'); },
  });
  const [note, setNote] = useState('');

  const p = profile.data;
  const name = p?.subscriber.name ?? '';
  const first = name.split(' ')[0] || 'them';
  const head = (
    <>
      <Top><Back to={`/s/${id}`} label={name ? first : 'Back'} /></Top>
      <Title h={`${name ? `${first}’s` : 'Their'} My plan page`} sub="Their own page. No app, no sign-in." />
    </>
  );
  if (q.isError) return <Screen gap={18}>{head}<LoadError what="the link" onRetry={() => void q.refetch()} /></Screen>;
  if (!q.data || !p) return <Screen gap={18}>{head}<PageLoader label="Making the link" /></Screen>;

  const link = q.data.link;
  const sm = p.summary;
  const due = p.payment?.due ?? 0;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setNote('Link copied.'); } catch { setNote('Couldn’t copy. Press and hold the link to copy it.'); }
  };
  const share = async () => {
    try { await navigator.share({ title: `${p.subscriber.name}’s plan`, text: q.data.text }); } catch { /* closed the share sheet */ }
  };

  const month = p.calendar?.month ?? '';
  const pausedThisMonth = month ? new Set(p.pauses.filter((x) => x.day.startsWith(month)).map((x) => x.day)).size : 0;
  const can = ['Pay by UPI', 'Ask to pause', `See ${first === 'them' ? 'their' : 'the'} calendar`, 'Order from Menus'];

  return (
    <Screen gap={18}>
      {head}

      {sm && p.subscription && (
        <Hero label={`What ${first} sees`} style={{ gap: 10 }}>
          <Kick s>What {first} sees</Kick>
          <b style={{ fontSize: 20, letterSpacing: '-0.02em' }}>{sm.left} {sm.left === 1 ? 'meal' : 'meals'} left · ends {formatDay(sm.endDate)}</b>
          <span className="p-muted">{[due > 0 ? `${formatInr(due)} to pay` : 'Paid up', pausedThisMonth ? `${pausedThisMonth} ${pausedThisMonth === 1 ? 'day' : 'days'} paused this month` : null, `pause requests by ${clock12(cutoff)} the day before`].filter(Boolean).join(' · ')}</span>
        </Hero>
      )}

      <div className="p-chips" aria-label={`On their page ${first} can`}>{can.map((c) => <span key={c} className="p-chip">{c}</span>)}</div>

      <Btn label="Send on WhatsApp" go="send" href={q.data.wa} />
      <span className="p-hint">To {formatPhone(p.subscriber.phone)}. You tap Send in WhatsApp; nothing goes out by itself.</span>

      <PCard>
        <Row onClick={() => void copy()} left={<Ic icon="sheet" />} t="Copy the link" s={<span className="p-1l">{link.replace(/^https?:\/\//, '')}</span>} />
        <Row href={link} left={<Ic icon="arrow" />} t={`Open ${first === 'them' ? 'their' : `${first}’s`} page`} chev />
        {canShare && <Row onClick={() => void share()} left={<Ic icon="send" />} t="Share another way" s="SMS, email or any app" />}
        <Row onClick={async () => { if (await confirm({ title: 'Make a new link?', body: `The link ${first} has now will stop working. Send them the new one after.`, yes: 'Make a new link', danger: true })) reset.mutate(); }}
          left={<Ic icon="refresh" />} t={reset.isPending ? 'Making a new link…' : 'Make a new link'} s="The old link stops working" disabled={reset.isPending} />
      </PCard>
      {note && <span className="p-status" role="status">{note}</span>}
      {reset.error && <span className="p-error" role="alert">{errorMessage(reset.error)}</span>}

      <Group label="The message">
        <div className="p-msg"><WhatsAppText text={q.data.text} /></div>
      </Group>
      <Note>The same link works for every plan {first} has with you, renewals included. It shows no phone numbers, notes or other customers.</Note>
      {confirmSheet}
    </Screen>
  );
}
